import { eventTypes } from "@/components/apprenticeship/candidate/utils";
import { AppointmentDialog } from "@/components/apprenticeship/shared/appointment-dialog";
import { AppointmentLinkDialog } from "@/components/apprenticeship/shared/appointment-link-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { WithIcon } from "@/components/utils/with-icon";
import { cn } from "@/lib/utils";
import { ApprenticeshipRegistrationType } from "@/types/apprenticeship/apprentice";
import { ApprenticeshipAppointmentType } from "@/types/apprenticeship/enterprise";
import { PaginatedResponse } from "@/types/common";
import { ExternalLink, MonitorPlay } from "lucide-react";
import { DateTime } from "luxon";
import { NavLink, useSearchParams } from "react-router-dom";
import { AppointmentCommentDialog } from "../shared/appointment-comment-dialog";
import AppointmentConfirmDialog from "@/components/apprenticeship/shared/appointment-confirm-dialog";
import { useUser } from "@/providers/user";
import { AppointmentOutcomeDialog } from "@/components/apprenticeship/shared/appointment-outcome-dialog";
import AppointmentAbsentDialog from "@/components/apprenticeship/shared/appointment-absent-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import AppointmentCancelDialog from "@/components/apprenticeship/shared/appointment-cancel-dialog";

export function Appointments({
    registration,
    appointments,
    type = "full"
}: {
    appointments: PaginatedResponse<ApprenticeshipAppointmentType> | undefined;
    registration: ApprenticeshipRegistrationType;
    type?: string;
}) {
    const { has_any_group } = useUser();
    const [searchParams, _] = useSearchParams();
    const is_admin = has_any_group(["app_apprenticeship_admin"]);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    <div>Rendez-vous{appointments?.count ? ` (${appointments?.count})` : ""}</div>
                    <AppointmentDialog registration={registration} size={20} />
                </CardTitle>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead>Date et heure</TableHead>
                            <TableHead>Intervenant</TableHead>
                            {type === "full" && (
                                <>
                                    <TableHead>Commentaire</TableHead>
                                    <TableHead>Résultat entretien</TableHead>
                                    <TableHead>Actions</TableHead>
                                </>
                            )}
                        </TableRow>
                    </TableHeader>

                    <TableBody>
                        {appointments?.results.map(appointment => (
                            <TableRow key={appointment.id} className={`${searchParams?.get("appointment_id") == appointment.id.toString() ? "bg-secondary" : undefined} `}>
                                <TableCell>
                                    <NavLink
                                        target="_blank"
                                        to={`https://app.walter-learning.com/admin_dashboard/mentoring/planning?page=1&page_size=25&start_date=${DateTime.fromISO(appointment.dt_start).toISODate()}`}
                                    >
                                        <WithIcon icon={ExternalLink} trailing>
                                            {eventTypes.find(e => e.code === appointment.event_type)?.name ?? "-"}
                                        </WithIcon>
                                    </NavLink>
                                </TableCell>
                                <TableCell className={cn(appointment.cancelled ? "line-through" : "")}>
                                    <span>
                                        {DateTime.fromISO(appointment.dt_start).toLocaleString(DateTime.DATETIME_MED)} - {DateTime.fromISO(appointment.dt_end).toLocaleString(DateTime.TIME_SIMPLE)}
                                    </span>
                                </TableCell>
                                <TableCell className={cn(appointment.cancelled && "line-through")}>
                                    {appointment.host?.first_name} {appointment.host?.last_name}
                                </TableCell>
                                {type === "full" && (
                                    <>
                                        <TableCell>{appointment.comment}</TableCell>
                                        <TableCell>
                                            <AppointmentOutcomeDialog appointment={appointment} size={18} />
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100">
                                                    <span className="sr-only">Actions</span>
                                                    <DotsHorizontalIcon className="h-5 w-5" />
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                                    <DropdownMenuSeparator />
                                                    {!appointment.cancelled && DateTime.fromISO(appointment.dt_start) > DateTime.now().minus({ hours: 6 }) && (
                                                        <DropdownMenuItem onSelect={e => e.preventDefault()}>
                                                            <AppointmentDialog registration={registration} appointment={appointment}>
                                                                <span>Modifier le rendez-vous</span>
                                                            </AppointmentDialog>
                                                        </DropdownMenuItem>
                                                    )}
                                                    {is_admin && appointment.event_type.includes("onboarding") == false && (
                                                        <DropdownMenuItem onSelect={e => e.preventDefault()}>
                                                            <AppointmentConfirmDialog appointment={appointment} size={18}>
                                                                <span>Confirmer la présence</span>
                                                            </AppointmentConfirmDialog>
                                                        </DropdownMenuItem>
                                                    )}

                                                    {is_admin && (
                                                        <DropdownMenuItem onSelect={e => e.preventDefault()}>
                                                            <AppointmentCancelDialog appointment={appointment} size={18}>
                                                                <span>Annuler le rendez-vous</span>
                                                            </AppointmentCancelDialog>
                                                        </DropdownMenuItem>
                                                    )}

                                                    {appointment.event_type.includes("onboarding") && (
                                                        <DropdownMenuItem onSelect={e => e.preventDefault()}>
                                                            <AppointmentAbsentDialog appointment={appointment} size={18}>
                                                                <span>Marquer comme absent</span>
                                                            </AppointmentAbsentDialog>
                                                        </DropdownMenuItem>
                                                    )}

                                                    {is_admin && (
                                                        <DropdownMenuItem onSelect={e => e.preventDefault()}>
                                                            <AppointmentCommentDialog appointment={appointment} size={18}>
                                                                <span>Modifier le commentaire</span>
                                                            </AppointmentCommentDialog>
                                                        </DropdownMenuItem>
                                                    )}
                                                    <DropdownMenuItem onSelect={e => e.preventDefault()}>
                                                        <AppointmentLinkDialog appointment={appointment} size={18}>
                                                            <span>Lien pour accéder au rendez-vous</span>
                                                        </AppointmentLinkDialog>
                                                    </DropdownMenuItem>

                                                    {is_admin && appointment.recording_url && (
                                                        <DropdownMenuItem>
                                                            <a href={appointment.recording_url} target="_blank" className="flex w-full items-center">
                                                                <MonitorPlay size={18} className="mr-2" />
                                                                Accéder au replay
                                                            </a>
                                                        </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}
